/**
 * 权限信息缓存管理器
 * 负责管理用户权限信息的本地缓存，包括保存、读取、清除和过期机制
 */

import type { PermissionInfo } from '../types/models';

export class PermissionCache {
  private static readonly CACHE_KEY = 'permission_info_v2';
  private static readonly CACHE_EXPIRY_KEY = 'permission_info_v2_expiry';
  /** 30分钟缓存 */
  private static readonly CACHE_DURATION = 30 * 60 * 1000;
  /** 5 分钟内不重复刷新（staleness 阈值） */
  private static readonly FRESH_DURATION = 5 * 60 * 1000;

  static save(info: PermissionInfo): void {
    try {
      const expiry = Date.now() + this.CACHE_DURATION;
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(info));
      localStorage.setItem(this.CACHE_EXPIRY_KEY, expiry.toString());
    } catch (error) {
      console.error('保存权限信息到缓存失败:', error);
    }
  }

  static get(): PermissionInfo | null {
    try {
      const expiryStr = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (!expiryStr) return null;

      const expiry = parseInt(expiryStr, 10);
      if (Date.now() > expiry) {
        this.clear();
        return null;
      }

      const cached = localStorage.getItem(this.CACHE_KEY);
      if (!cached) return null;

      return JSON.parse(cached) as PermissionInfo;
    } catch {
      this.clear();
      return null;
    }
  }

  static clear(): void {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_EXPIRY_KEY);
  }

  static isCacheValid(): boolean {
    try {
      const expiryStr = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (!expiryStr) return false;
      return Date.now() <= parseInt(expiryStr, 10);
    } catch {
      return false;
    }
  }

  /** 缓存仍在有效期内但是否已过"新鲜期"（5 分钟）。
   * 用于窗口重新获焦时判断是否需要后台刷新。
   */
  static isStale(): boolean {
    try {
      const expiryStr = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      if (!expiryStr) return true;
      const expiry = parseInt(expiryStr, 10);
      const age = expiry - this.CACHE_DURATION;
      return Date.now() - age > this.FRESH_DURATION;
    } catch {
      return true;
    }
  }
}

